import { apiRequest } from '../lib/fetchWithAuth';
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

type ContentType = 'headlines' | 'comments' | 'attachments';

export function DeleteButton({
  contentType,
  contentId,
  organizationId,
  hasComments,
  label = 'Delete',
  onDeleted
}: {
  contentType: ContentType;
  contentId: string;
  organizationId?: string;
  hasComments?: boolean;
  label?: string;
  onDeleted?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const queryClient = useQueryClient();

  const { data: perm } = useQuery({
    queryKey: ['del-perm', contentType, contentId, organizationId],
    queryFn: async () => {
      let url = `/api/deletion/permissions/${contentType}/${contentId}`;
      if (organizationId) url += `?organizationId=${encodeURIComponent(organizationId)}`;
      const res = await fetch(url, { credentials: 'include', redirect: 'error' });
      return res.json();
    },
    staleTime: 0
  });

  const del = useMutation({
    mutationFn: async () => {
      let url = `/api/deletion/${contentType}/${contentId}`;
      if (organizationId) url += `?organizationId=${encodeURIComponent(organizationId)}`;
      return apiRequest(url, {
        method: 'DELETE',
        data: { reason, cascadeComments: hasComments }
      });
    },
    onSuccess: () => {
      setOpen(false);
      queryClient.invalidateQueries();
      onDeleted?.();
    }
  });

  if (!perm?.allowed) return null;

  return (
    <div className="inline-flex items-center gap-2">
      <button
        onClick={() => setOpen(true)}
        className="text-red-600 hover:text-red-700 text-xs"
      >
        <span className="mdi mdi-delete mr-1" /> {label}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-neutral-900 border rounded-lg p-4 w-full max-w-sm">
            <div className="font-semibold mb-2">Confirm Deletion</div>
            {hasComments && (
              <div className="text-xs text-amber-600 mb-2">This post has comments. Deleting will also remove all comments.</div>
            )}
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (optional)"
              className="w-full border rounded p-2 text-sm mb-3 bg-transparent"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="px-3 py-1.5 text-sm border rounded">Cancel</button>
              <button onClick={() => del.mutate()} className="px-3 py-1.5 text-sm rounded bg-red-600 text-white disabled:opacity-50" disabled={del.isPending}>
                {del.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


